import { useCallback, useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import { RetailerHeaderModel } from '../../../../app/services/api/ofdClient';

interface RetailersByOnboardingStatusProps {
  retailers: RetailerHeaderModel[];
}

const RetailersByOnboardingStatus = ({ retailers }: RetailersByOnboardingStatusProps) => {
  const theme = useTheme();

  const data = useMemo(() => {
    // Example: statusId 3 = Onboarded, 2 = In Progress, 1 = Not Started (adjust as needed)
    const statusCounts = {
      Onboarded: 0,
      InProgress: 0,
    };
    retailers.forEach(r => {
      if (r.statusId === 2) {
        statusCounts.Onboarded++;
      } else if (r.statusId === 1) {
        statusCounts.InProgress++;
      }
    });

    return [
      { name: 'Onboarded', value: statusCounts.Onboarded },
      { name: 'In Progress', value: statusCounts.InProgress },
    ];
  }, [retailers]);

  const COLORS = useMemo(() => ['#0088FE', '#00C49F', '#FFBB28'], []);

  // Custom label renderer to prevent truncation
  const renderCustomLabel = useCallback(({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }: { cx: number, cy: number, midAngle: number, innerRadius: number, outerRadius: number, percent: number, index: number }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.1;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const name = data[index].name;
    return (
      <text
        x={x}
        y={y}
        fill={theme.palette.text.primary}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: theme.typography.textSM?.fontSize || 12, fontFamily: theme.typography.fontFamily }}
      >
        {`${name} (${(percent * 100).toFixed(0)}%)`}
      </text>
    );
  }, [data, theme]);

  return (
    <Box sx={{ height: 260 }}>
      <ResponsiveContainer width="100%"
        height={200}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={85}
            fill={theme.palette.primary.main}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell
                key={entry.name}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip contentStyle={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.textSM.fontSize,
          }} />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default RetailersByOnboardingStatus;